// @flow
import * as React from 'react';
import styled from 'styled-components';
import { range } from 'utils/functional';
import type {
  ThreeSectionSliderImageSizes,
  ThreeSectionSliderBlockData
} from 'api/cms';
import type { ImageData } from 'api/shared';
import Flex from 'components/Flex';
import Box from 'components/Box';
import Icon from 'components/Icon';
import Button, { DotNavigationButton } from 'components/Button';
import { RichTextHeadline, BodySmall } from 'components/Text';
import Carousel, { Slide } from 'containers/Carousel';
import { FilestackImage } from 'components/Image';
import { colors, transition, breakpoints } from '../../theme';

const IMAGE_SIZES = {
  SMALL: 80,
  MEDIUM: 130
};

const ImageContainer = styled(Box)`
  img {
    max-width: 100%;
  }
  @supports (object-fit: contain) {
    img {
      max-width: none;
      width: 100%;
      object-fit: contain;
    }
  }
`;

const ArrowNavigation = styled(
  ({ className, nextPage, prevPage, current, total, ...props }) => {
    const title = {
      prev: getText('Previous page'),
      next: getText('Next page')
    };
    const prevDisabled = current <= 0;
    const nextDisabled = current >= total - 1;
    return (
      <Flex
        justifyContent="space-between"
        alignItems="center"
        className={className}
        {...props}
      >
        <Button
          onClick={prevPage}
          aria-label={title.prev}
          title={title.prev}
          disabled={prevDisabled}
          data-qa-id="carousel-left-button"
        >
          <Box transform="translateX(-1px)">
            <Icon
              name="chevron"
              direction="left"
              fill="currentColor"
              display="block"
            />
          </Box>
        </Button>
        <Button
          onClick={nextPage}
          aria-label={title.next}
          title={title.next}
          disabled={nextDisabled}
          data-qa-id="carousel-right-button"
        >
          <Box transform="translateX(1px)">
            <Icon
              name="chevron"
              direction="right"
              fill="currentColor"
              display="block"
            />
          </Box>
        </Button>
      </Flex>
    );
  }
)`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  z-index: 2;
  width: calc(100% + 128px);
  padding: 0 12px;
  max-width: calc(100vw - 16px);
  button {
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 50%;
    color: ${colors.waterBlue};
    transition: all ${transition};
    svg {
      * {
        fill: currentColor;
      }
    }
    &[disabled] {
      opacity: 0.33;
    }
    @media (min-width: ${breakpoints[2]}) {
      width: 40px;
      height: 40px;
      padding: 0;
      color: white;
      background: ${colors.waterBlue};
      &[disabled] {
        opacity: 0.33;
        &:hover {
          background: ${colors.waterBlue};
        }
      }
      &:hover,
      &:focus {
        background: ${colors.turtleGreen};
      }
    }
  }
`;

const CarouselContainer = styled(Box)`
  position: relative;
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    z-index: 1;
    height: 100%;
    width: 2px;
    background: white;
  }
`;

const Section = ({
  imageQaId,
  image,
  imageSize,
  text,
  withImages,
  ...props
}: {
  imageQaId: string,
  image: ?ImageData,
  imageSize: ThreeSectionSliderImageSizes,
  text: string,
  withImages: boolean
}) => (
  <Box flex="0 0 auto" width="100%" height="100%" {...props}>
    <Box
      maxWidth="320px"
      display="flex"
      height="100%"
      alignItems="center"
      justifyContent="flex-start"
      flexDirection="column"
      width="100%"
      m="0 auto"
      p={['24px 20px', '24px 0', '0 32px']}
    >
      {(image || withImages) && (
        <ImageContainer height={IMAGE_SIZES[imageSize] + 26} width="100%">
          {image && (
            <FilestackImage
              data-qa-id={imageQaId}
              alt={text.slice(0, 100)}
              data={image}
              height={IMAGE_SIZES[imageSize]}
              quality={imageSize === 'SMALL' ? 100 : undefined}
            />
          )}
        </ImageContainer>
      )}
      <Box width="100%" textAlign="center">
        <BodySmall dangerouslySetInnerHTML={{ __html: text }} />
      </Box>
    </Box>
  </Box>
);

const ThreeSectionSlider = ({
  data: { headline, imageSize, sections }
}: {
  data: ThreeSectionSliderBlockData
}) => {
  const withImages = sections.some(section => section.image);
  return (
    <>
      {headline && (
        <RichTextHeadline textAlign="center">{headline}</RichTextHeadline>
      )}
      <CarouselContainer mt="40px">
        <Carousel
          data-qa-id="three-section-slider-carousel"
          pageLength={[1, 1, 2, 3]}
          navigation={[
            ({
              current,
              total,
              goToPage,
              prevPage,
              nextPage
            }: {
              current: number,
              total: number,
              goToPage: Function,
              prevPage: Function,
              nextPage: Function
            }) => (
              <nav>
                {total > 1 && (
                  <>
                    <ArrowNavigation
                      nextPage={nextPage}
                      prevPage={prevPage}
                      current={current}
                      total={total}
                    />
                    <Flex mt="20px" justifyContent="center">
                      {range(total).map(index => (
                        <DotNavigationButton
                          // eslint-disable-next-line react/no-array-index-key
                          key={index}
                          goToPage={goToPage}
                          index={index}
                          isActive={index === current}
                        />
                      ))}
                    </Flex>
                  </>
                )}
              </nav>
            )
          ]}
          slides={() =>
            sections.map((section, index) => (
              <Slide
                // eslint-disable-next-line react/no-array-index-key
                key={`${'section'}-${index}`}
                height="100%"
              >
                <Section
                  imageQaId={`slider-image-${index}`}
                  text={section.content}
                  image={section.image}
                  imageSize={imageSize}
                  withImages={withImages}
                  borderLeft={[
                    null,
                    null,
                    index > 0 ? `1px solid ${colors.silver}` : undefined
                  ]}
                />
              </Slide>
            ))
          }
        />
      </CarouselContainer>
    </>
  );
};

export default ThreeSectionSlider;
